import { ActionIcon, Checkbox, Tooltip } from "@mantine/core";
import { IconEdit, IconHistory, IconTrash } from "@tabler/icons-react";
import React from "react";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { usePostApi } from "src/hooks";
import { Flex, StyledTd, StyledTr } from "src/styled";
import { Notification } from "src/utils/Notification";

import { formatTankLimits } from "../tankLimits.utils";
import {
  type actionType,
  type ITankPlan,
  type IValuePair,
  orbs,
  tankTypes,
} from "../tankPlans.model";
import styles from "./TankPlansTableRow.module.scss";

const StyledTankPlansTd: React.FC<React.ComponentProps<typeof StyledTd>> = ({
  className,
  ...props
}) => (
  <StyledTd
    className={`${styles.styledTankPlansTd} ${className ?? ""}`}
    {...props}
  />
);

interface Props extends ITankPlan {
  onAction: (action: actionType, rowIdx: number) => void;
  index: number;
  refetch: () => void;
}

const TankPlansTableRow = ({
  name,
  type,
  capacity,
  limits,
  bwl,
  mfsbA,
  mfsbB,
  orb,
  onAction,
  index,
  tankid,
  refetch,
}: Props) => {
  const { post } = usePostApi();

  const getValue = (array: IValuePair[], value: string) =>
    array.find(el => el.value === value)?.label;

  const limitsLabel = formatTankLimits(limits);

  const handleDelete = () => {
    void post({
      request: "remove-tank",
      body: { params: { tankid } },
      onSuccess: () => {
        Notification("Success", "Tank removed successfully", "green");
        refetch();
      },
    });
  };

  return (
    <StyledTr>
      <StyledTankPlansTd width="8%">
        {getValue(tankTypes, type)}
      </StyledTankPlansTd>
      <StyledTankPlansTd width="20%">{name}</StyledTankPlansTd>
      <StyledTankPlansTd width="6%">
        {capacity.value} ({capacity.unit})
      </StyledTankPlansTd>
      <StyledTankPlansTd width="12%">
        <Tooltip withArrow label={limitsLabel} disabled={limitsLabel === "—"}>
          <span>{limitsLabel}</span>
        </Tooltip>
      </StyledTankPlansTd>
      <StyledTankPlansTd width="30%">
        {Array.isArray(orb)
          ? orb.map(value => getValue(orbs, value)).join(", ")
          : orb}
      </StyledTankPlansTd>
      {[bwl, mfsbA, mfsbB].map((el, i) => (
        <StyledTankPlansTd
          key={`checkbox-${i}`}
          width={el === bwl ? "130px" : "150px"}>
          <Checkbox
            checked={el}
            onChange={() => {}}
            style={{ display: "flex", justifyContent: "center" }}
          />
        </StyledTankPlansTd>
      ))}
      <StyledTankPlansTd className={styles.actionsCell}>
        <Flex
          gap={5}
          direction="row"
          justify="flex-end"
          align="center"
          w="fit-content"
          ml="auto">
          <Tooltip withArrow label="Edit">
            <ActionIcon
              size={32}
              variant="outline"
              color="blue"
              onClick={() => onAction("add", index)}>
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip withArrow label="History">
            <ActionIcon
              size={32}
              variant="outline"
              color="blue"
              onClick={() => onAction("history", index)}>
              <IconHistory size={18} />
            </ActionIcon>
          </Tooltip>
          <ConfirmationButton
            actionIcon
            modalText="Are you sure you wish to remove this tank plan?"
            onConfirm={handleDelete}
            actionIconProps={{ size: 32, variant: "outline", color: "red" }}>
            <Tooltip withArrow label="Delete">
              <IconTrash size={18} />
            </Tooltip>
          </ConfirmationButton>
        </Flex>
      </StyledTankPlansTd>
    </StyledTr>
  );
};

export default TankPlansTableRow;
